import React from "react";
import WebProfitSpinner from "../../components/system/WebProfitSpinner";
import { Alert, Box, Button, Link, Stack } from "@mui/material";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { authApi } from "../../api/auth";
import {
  AuthPageShell,
  AuthPasswordField,
  authLinkSx,
  authPrimaryButtonSx,
} from "../../components/dashboard/auth/AuthFormShell";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = String(searchParams.get("token") || "").trim();
  const [form, setForm] = React.useState({ newPassword: "", confirmPassword: "" });
  const [message, setMessage] = React.useState("");
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [completed, setCompleted] = React.useState(false);

  const handleChange = (event) => {
    setForm((previous) => ({ ...previous, [event.target.name]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!token) {
      setError("El enlace de recuperación no es válido.");
      return;
    }
    if (form.newPassword.length < 8) {
      setError("La nueva contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      setError("Las contraseñas no coinciden.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      const data = await authApi.resetPassword({ token, newPassword: form.newPassword });
      setCompleted(true);
      setMessage(data?.message || "Contraseña actualizada. Ya podés iniciar sesión.");
      setForm({ newPassword: "", confirmPassword: "" });
    } catch (err) {
      setError(err.message || "No se pudo restablecer la contraseña.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPageShell
      title="Nueva contraseña"
      subtitle={completed ? "Tu contraseña ya fue actualizada." : "Usá al menos 8 caracteres."}
      icon={<LockResetRoundedIcon />}
      helpLabel="Sobre este cambio"
      help="El enlace de recuperación funciona una sola vez. Al cambiar la contraseña, las sesiones anteriores dejan de ser válidas por seguridad."
      footer={
        <Link component={RouterLink} to={token ? "/login" : "/olvide-contrasena"} sx={authLinkSx}>
          {token ? "Volver a ingresar" : "Solicitar otro enlace"}
        </Link>
      }
    >
      {!token ? <Alert severity="error">El enlace no es válido o está incompleto. Solicitá uno nuevo.</Alert> : null}
      {message ? <Alert severity="success">{message}</Alert> : null}
      {error ? <Alert severity="error">{error}</Alert> : null}

      {!completed && token ? (
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={1.5}>
            <AuthPasswordField
              label="Nueva contraseña"
              name="newPassword"
              value={form.newPassword}
              onChange={handleChange}
              autoComplete="new-password"
              disabled={loading}
              helperText="Mínimo 8 caracteres."
              autoFocus
            />
            <AuthPasswordField
              label="Repetir contraseña"
              name="confirmPassword"
              value={form.confirmPassword}
              onChange={handleChange}
              autoComplete="new-password"
              disabled={loading}
            />
            <Button type="submit" disabled={loading} variant="contained" size="large" startIcon={loading ? <WebProfitSpinner size={17} /> : undefined} sx={authPrimaryButtonSx}>
              {loading ? "Guardando..." : "Guardar contraseña"}
            </Button>
          </Stack>
        </Box>
      ) : completed ? (
        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={() => navigate("/login", { replace: true, state: { message: "Contraseña actualizada correctamente." } })}
          sx={authPrimaryButtonSx}
        >
          Iniciar sesión
        </Button>
      ) : null}
    </AuthPageShell>
  );
}
